import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/DashboardLayout';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import axios from 'axios';
import { Save, Plus, Trash2, LayoutTemplate } from 'lucide-react';
import { useCallback } from 'react';

const AdminCareerChavdi = () => {
    const { showSuccess, showError } = useNotification();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const {
        register,
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            title: '',
            subtitle: '',
            description: '',
            mainPageDescription: '',
            points: [''],
            videoUrl: '',
            quote: ''
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'points'
    });

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/career-chavdi');
            if (response.success && response.data) {
                const fetchedData = response.data;
                reset(fetchedData);
                // Handle case where points array might be empty from backend
                if (!fetchedData.points || fetchedData.points.length === 0) {
                    reset({ ...fetchedData, points: [''] });
                }
            }
        } catch (error) {
            console.error('Error fetching Career Chavdi data:', error);
            showError('Failed to load Career Chavdi content.');
        } finally {
            setIsLoading(false);
        }
    }, [reset, showError]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSubmit = async (data) => {
        try {
            setIsSaving(true);
            // Filter out any empty points before saving
            const cleanedData = {
                ...data,
                points: data.points.filter(p => p.trim() !== '')
            };

            await api.put('/career-chavdi', cleanedData);
            showSuccess('Career Chavdi section updated successfully!');
            fetchData(); // Refresh to get exact saved state
        } catch (error) {
            console.error('Error saving Career Chavdi data:', error);
            showError('Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Basic validation for video type
        if (!file.type.startsWith('video/')) {
            showError('Please select a valid video file.');
            return;
        }

        try {
            setIsUploadingVideo(true);
            setUploadProgress(0);
            
            // 1. Get the Cloudinary signature from our backend
            const sigResponse = await api.get('/upload/signature');
            if (!sigResponse.success) {
                throw new Error('Failed to obtain upload signature');
            }
            
            const { timestamp, signature, apiKey, cloudName, folder } = sigResponse;

            // 2. Prepare form data for direct upload
            const formData = new FormData();
            formData.append('file', file);
            formData.append('api_key', apiKey);
            formData.append('timestamp', timestamp);
            formData.append('signature', signature);
            formData.append('folder', folder);

            // 3. Upload directly to Cloudinary using standard axios (bypasses our interceptors)
            const uploadRes = await axios.post(
                `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    }
                }
            );

            if (uploadRes.data && uploadRes.data.secure_url) {
                setValue('videoUrl', uploadRes.data.secure_url, { shouldValidate: true, shouldDirty: true });
                showSuccess('Video uploaded securely to Cloudinary!');
            } else {
                showError('Failed to format video upload response.');
            }
        } catch (error) {
            console.error('Error uploading video:', error);
            showError(error.response?.data?.error?.message || 'Failed to upload video.');
        } finally {
            setIsUploadingVideo(false);
            // Clear the input so the same file could be selected again if needed
            event.target.value = null;
        }
    };

    return (
        <ProtectedRoute roles={['ADMIN']}>
            <DashboardLayout title="Career Chavdi Settings" roles={['ADMIN']}>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 w-full">
                    <div className="flex items-center space-x-3 mb-8 pb-4 border-b border-gray-100">
                        <div className="p-2 bg-pink-50 text-[#E91E63] rounded-lg">
                            <LayoutTemplate className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Section Content</h2>
                            <p className="text-sm text-gray-500">Manage the text, video, and lists shown on the home page.</p>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E91E63]"></div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

                            {/* Headers */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subtitle (Pill Badge)
                                    </label>
                                    <input
                                        type="text"
                                        {...register('subtitle', { required: 'Subtitle is required' })}
                                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent ${errors.subtitle ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="e.g., Discover & Learn"
                                    />
                                    {errors.subtitle && <p className="mt-1 text-sm text-red-500">{errors.subtitle.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Inner Title
                                    </label>
                                    <input
                                        type="text"
                                        {...register('title', { required: 'Title is required' })}
                                        className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="e.g., Empowering Your Professional Journey"
                                    />
                                    {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>}
                                </div>
                            </div>

                            {/* Main Text */}
                            <div>
                                <div className="flex justify-between items-end mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Main Description Paragraph
                                    </label>
                                    <span className={`text-xs ${watch('description')?.length === 350 ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
                                        {watch('description')?.length || 0} / 350 chars
                                    </span>
                                </div>
                                <textarea
                                    {...register('description', {
                                        required: 'Description is required',
                                        maxLength: { value: 350, message: 'Maximum 350 characters allowed' }
                                    })}
                                    maxLength={350}
                                    rows="5"
                                    className={`w-full p-4 text-base resize-y custom-scrollbar border rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="Enter the main paragraph text..."
                                ></textarea>
                                {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>}
                            </div>

                            {/* Main Page Specific Text */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Main Description Paragraph (Standalone /career-chavdi Page Only)
                                </label>
                                <textarea
                                    {...register('mainPageDescription')}
                                    rows="5"
                                    className="w-full p-4 text-base resize-y custom-scrollbar border rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent border-gray-300"
                                    placeholder="Enter additional text to show only on the dedicated page (optional)..."
                                ></textarea>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Bullet Points */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Features List (Bullet Points)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => append('')}
                                        className="text-sm flex items-center text-[#E91E63] hover:text-[#c2185b] font-medium transition-colors"
                                    >
                                        <Plus className="w-4 h-4 mr-1" /> Add Point
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {fields.map((field, index) => (
                                        <div key={field.id} className="flex items-center gap-3">
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    {...register(`points.${index}`)}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent"
                                                    placeholder={`Point ${index + 1}`}
                                                />
                                            </div>
                                            {fields.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => remove(index)}
                                                    className="p-3 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Remove point"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Video / Quote */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Video File
                                    </label>

                                    <input
                                        type="file"
                                        accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
                                        onChange={handleVideoUpload}
                                        disabled={isUploadingVideo}
                                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-[#E91E63] hover:file:bg-pink-100 transition-colors"
                                    />

                                    <input type="hidden" {...register('videoUrl', { required: 'Video is required' })} />
                                    {errors.videoUrl && <p className="mt-1 text-sm text-red-500">{errors.videoUrl.message}</p>}

                                    {isUploadingVideo && (
                                        <div className="mt-3 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                            <div className="flex justify-between items-center text-sm text-blue-700 mb-2">
                                                <div className="flex items-center font-medium">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                                    Uploading Video...
                                                </div>
                                                <span className="font-bold">{uploadProgress}%</span>
                                            </div>
                                            <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300 relative"
                                                    style={{ width: `${uploadProgress}%` }}
                                                >
                                                    <div className="absolute top-0 right-0 bottom-0 left-0 bg-white opacity-20 animate-pulse"></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {watch('videoUrl') && !isUploadingVideo && (
                                        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                            <p className="text-sm font-medium text-gray-700 mb-2">Current Video Preview:</p>
                                            <video
                                                src={watch('videoUrl')}
                                                controls
                                                className="w-full rounded-lg max-h-48 object-cover object-center bg-black"
                                                alt="Section Video"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Quote Overlay Text
                                    </label>
                                    <textarea
                                        {...register('quote')}
                                        rows="3"
                                        className="w-full p-4 text-base resize-y custom-scrollbar border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent"
                                        placeholder={`"Education is not the learning of facts..."`}
                                    ></textarea>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end pt-6 border-t border-gray-100">
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-6 py-3 bg-[#E91E63] text-white rounded-lg hover:bg-[#c2185b] focus:ring-4 focus:ring-pink-200 font-medium flex items-center transition-all disabled:opacity-70"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Saving Changes...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5 mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </DashboardLayout>
        </ProtectedRoute>
    );
};

export default AdminCareerChavdi;
